// Client Projects Workspace — shared types and DTOs
// Requirements: 3.1, 6.1, 6.3, 7.2, 7.3

// --- Status & Enum Types ---

export type ClientProjectStatus =
  | 'in_production'
  | 'awaiting_review'
  | 'revision_requested'
  | 'approved'
  | 'delivered'
  | 'blocked'
  | 'waiting_on_client';

export type AttentionItemType =
  | 'approval_required'
  | 'unread_message'
  | 'new_upload'
  | 'revision_request'
  | 'overdue';

export type ClientSortOption =
  | 'recent_activity'
  | 'last_updated'
  | 'due_date'
  | 'alphabetical'
  | 'recently_delivered'
  | 'priority'
  | 'approval_needed';

export type ClientViewMode = 'list' | 'grid';

// --- Status Tab Configuration ---

export interface StatusTabConfig {
  id: string;
  label: string;
  statusFilter: ClientProjectStatus[] | null; // null = all
}

export const STATUS_TAB_CONFIGS: StatusTabConfig[] = [
  { id: 'all', label: 'All Projects', statusFilter: null },
  { id: 'active', label: 'Active', statusFilter: ['in_production', 'awaiting_review', 'revision_requested', 'waiting_on_client', 'blocked'] },
  { id: 'awaiting_review', label: 'Awaiting Review', statusFilter: ['awaiting_review'] },
  { id: 'revision_requested', label: 'Revision Requested', statusFilter: ['revision_requested'] },
  { id: 'delivered', label: 'Delivered', statusFilter: ['delivered', 'approved'] },
  { id: 'archived', label: 'Archived', statusFilter: [] },
] as const;

// --- DTOs ---

export interface ClientProjectSummaryDto {
  id: string;
  name: string;
  status: ClientProjectStatus;
  lastActivityAt: string; // ISO timestamp
  dueDate: string | null;
  priority: number;
  deliveredAt: string | null;
  thumbnailUrl: string | null;
  pendingApprovals: number;
  hasUnreadMessages: boolean;
  hasNewActivity: boolean;
  collaboratorCount: number;
  isRecentlyDelivered: boolean; // computed server-side (within 30 days)
}

export interface ClientProjectDetailDto extends ClientProjectSummaryDto {
  collaborators: { id: string; name: string; avatarUrl: string | null }[];
  tags: string[];
  skus: string[];
}

export interface AttentionItemDto {
  id: string; // synthetic ID: `${projectId}:${type}`
  type: AttentionItemType;
  sourceName: string;
  sourceProjectId: string;
  actionableAt: string; // ISO timestamp
}

export interface ActivityFeedEntryDto {
  id: string;
  projectId: string;
  eventType: string;
  actorName: string | null;
  metadata: Record<string, unknown>;
  createdAt: string; // ISO timestamp
}

// --- API Response Types ---

export interface ClientProjectsListResponse {
  success: true;
  data: {
    projects: ClientProjectSummaryDto[];
    attentionItems: AttentionItemDto[];
    statusCounts: Record<ClientProjectStatus | 'all', number>;
    lastLoadedAt: string | null;
  };
}

export interface ClientProjectDetailResponse {
  success: true;
  data: {
    project: ClientProjectDetailDto;
    activityFeed: ActivityFeedEntryDto[];
    activityCursor: string | null;
  };
}

export interface ClientActivityPageResponse {
  success: true;
  data: {
    entries: ActivityFeedEntryDto[];
    nextCursor: string | null;
  };
}

export interface ClientApproveResponse {
  success: true;
  data: { project: ClientProjectSummaryDto };
}

export interface ClientRevisionRequestResponse {
  success: true;
  data: { project: ClientProjectSummaryDto };
}

export interface ClientDismissAttentionResponse {
  success: true;
}

// --- Error Types ---

export type ClientWorkspaceErrorCode =
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'WORKSPACE_REQUIRED'
  | 'PROJECT_NOT_FOUND'
  | 'CONFLICT'
  | 'INVALID_INPUT'
  | 'SEARCH_FAILED'
  | 'INTERNAL_ERROR';

export interface ClientWorkspaceErrorResponse {
  success: false;
  error: {
    code: ClientWorkspaceErrorCode;
    message: string;
  };
}
